import React, { useState } from 'react';
import { X, Send, CheckCircle2, MessageCircle, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ContactFormModal({ isOpen, onClose, isExec, defaultSubject }) {
  const [form, setForm] = useState({ name: '', email: '', subject: defaultSubject || 'general', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/waitlist/contact`, form);
      setSent(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to send. Please try again.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSent(false);
    setForm({ name: '', email: '', subject: defaultSubject || 'general', message: '' });
    onClose();
  };

  const subjects = [
    { value: 'general', label: isExec ? 'General Inquiry' : 'GENERAL INTEL', icon: MessageCircle },
    { value: 'partnership', label: isExec ? 'Partnership' : 'ALLIANCE REQUEST', icon: Briefcase },
  ];

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={`relative w-full max-w-lg rounded-2xl border overflow-hidden ${
          isExec
            ? 'bg-white border-gray-200 shadow-2xl'
            : 'bg-[#0a0a0f] border-[#00FF64]/20 shadow-[0_0_60px_rgba(0,255,100,0.08)]'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`relative px-6 pt-6 pb-4 border-b ${isExec ? 'border-gray-100' : 'border-white/5'}`}>
          {!isExec && (
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00FF64]/40 to-transparent" />
          )}
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-mono tracking-[0.2em] mb-1 ${isExec ? 'text-gray-400' : 'text-[#00FF64]/60'}`}>
                {isExec ? 'GET IN TOUCH' : 'OPEN CHANNEL'}
              </p>
              <h3 className={`text-xl font-bold ${isExec ? 'text-gray-900' : 'text-white'}`}>
                {isExec ? 'Contact Us' : 'Send Transmission'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                isExec ? 'hover:bg-gray-100 text-gray-400' : 'hover:bg-white/5 text-gray-500'
              }`}
              data-testid="contact-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {sent ? (
          /* Success State */
          <div className="px-6 py-12 text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              isExec ? 'bg-green-50' : 'bg-[#00FF64]/10'
            }`}>
              <CheckCircle2 className={`w-8 h-8 ${isExec ? 'text-green-600' : 'text-[#00FF64]'}`} />
            </div>
            <h4 className={`text-lg font-bold mb-2 ${isExec ? 'text-gray-900' : 'text-white'}`}>
              {isExec ? 'Message Sent' : 'Transmission Received'}
            </h4>
            <p className={`text-sm mb-6 ${isExec ? 'text-gray-500' : 'text-gray-400'}`}>
              {isExec ? "We'll respond within 24 hours." : 'Command will respond within 24 hours.'}
            </p>
            <Button
              onClick={handleClose}
              className={`px-6 rounded-xl font-mono ${
                isExec
                  ? 'bg-gray-900 text-white hover:bg-gray-800'
                  : 'bg-[#00FF64] text-black hover:bg-[#00FF64]/90'
              }`}
              data-testid="contact-done-btn"
            >
              {isExec ? 'Done' : 'CLOSE CHANNEL'}
            </Button>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
            {/* Subject selector */}
            <div className="flex gap-2">
              {subjects.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, subject: s.value }))}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-mono tracking-wider border transition-all ${
                    form.subject === s.value
                      ? isExec
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-[#00FF64]/10 text-[#00FF64] border-[#00FF64]/40'
                      : isExec
                        ? 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300'
                        : 'bg-white/3 text-gray-500 border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`contact-subject-${s.value}`}
                >
                  <s.icon className="w-3.5 h-3.5" />
                  {s.label}
                </button>
              ))}
            </div>

            {/* Name */}
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={isExec ? 'Your Name' : 'AGENT ALIAS'}
              className={`w-full h-12 px-4 rounded-xl border text-sm font-mono focus:outline-none transition-colors ${
                isExec
                  ? 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-400'
                  : 'bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#00FF64]/30'
              }`}
              data-testid="contact-name"
            />

            {/* Email */}
            <input
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              required
              type="email"
              placeholder={isExec ? 'Email Address' : 'SECURE EMAIL'}
              className={`w-full h-12 px-4 rounded-xl border text-sm font-mono focus:outline-none transition-colors ${
                isExec
                  ? 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-400'
                  : 'bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#00FF64]/30'
              }`}
              data-testid="contact-email"
            />

            {/* Message */}
            <textarea
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              required
              rows={4}
              placeholder={isExec ? 'How can we help?' : 'ENTER YOUR MESSAGE...'}
              className={`w-full px-4 py-3 rounded-xl border text-sm font-mono focus:outline-none resize-none transition-colors ${
                isExec
                  ? 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-gray-400'
                  : 'bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-[#00FF64]/30'
              }`}
              data-testid="contact-message"
            />

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className={`w-full h-12 rounded-xl font-bold font-mono tracking-wider flex items-center justify-center gap-2 ${
                isExec
                  ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white hover:shadow-lg'
                  : 'bg-[#00FF64] text-black hover:shadow-[0_0_20px_rgba(0,255,100,0.3)]'
              }`}
              data-testid="contact-submit-btn"
            >
              <Send className="w-4 h-4" />
              {loading ? (isExec ? 'Sending...' : 'TRANSMITTING...') : (isExec ? 'Send Message' : 'SEND TRANSMISSION')}
            </Button>

            <p className={`text-[10px] text-center font-mono ${isExec ? 'text-gray-400' : 'text-gray-600'}`}>
              {isExec ? 'We typically respond within 24 hours.' : 'EXPECTED RESPONSE: 24 HOURS'}
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
